import { Col } from "react-bootstrap";

import { DollarIcon } from "../../../../assets/icons/iconsProvider";
import {
  EMPTY_STRING,
  SALE_TRANSACTION_ACTIVITY_TYPES,
  transactionConstants,
} from "../../../../constants";
import { SimpleNumberField } from "../../../../shared/components";
import {
  getPurchasePriceAfterSplitValueChange,
  getRandomNumber,
  parseToDecimalNumber,
  parseToNumber,
  parseToThousandSeperatorDecimalNumber,
  splitCalculation,
} from "../../../../shared/utility";
import { useRef } from "react";

const SplitPaymentInputFields = (props) => {
  const {
    splitAmounts,
    newTradeData,
    isCashPayment,
    isOtherPayment,
    updateTradeData,
  } = props;
  const inputFieldsKeyRef = useRef(getRandomNumber()); // It is used to force rerender of input fields. We must rerender the input fields on value change as when user enter value greater than max amount we round it down to max value. As the old and new value is same component does not rerender but in UI it keeps showing the user entered value in the input field so force rerender

  const handleSplitValueChange = (
    value,
    splitKey = "splitAmount",
    isOtherSplit = false
  ) => {
    const isCashOrOtherPayment = isCashPayment;
    const currentValue = newTradeData?.splitAmounts?.[splitKey];
    inputFieldsKeyRef.current = getRandomNumber();

    // Value didn't change
    if (Number(value) === Number(currentValue)) {
      updateTradeData(
        {
          ...newTradeData,
          splitAmounts: {
            ...splitAmounts,
            [splitKey]: value,
          },
        },
        SALE_TRANSACTION_ACTIVITY_TYPES.SPLIT_AMOUNT
      );
      return;
    }

    // Calculate opposite split value
    const valueAfterCalculation = splitCalculation(
      value,
      isCashOrOtherPayment,
      newTradeData,
      isOtherSplit
    );

    // Calculate purchase price after split value change
    const updatedInventory = getPurchasePriceAfterSplitValueChange(
      isCashOrOtherPayment
        ? isOtherSplit
          ? value
          : valueAfterCalculation
        : isOtherSplit
        ? valueAfterCalculation
        : value,
      isCashOrOtherPayment
        ? isOtherSplit
          ? valueAfterCalculation
          : value
        : isOtherSplit
        ? value
        : valueAfterCalculation,
      newTradeData?.inventory
    );

    // Calculate total amount to pay
    const totalAmount = newTradeData?.isSplitPayment
      ? parseToNumber(Number(valueAfterCalculation) + Number(value))
      : newTradeData?.totalAmountToPay;

    // Calculate cash amount to pay
    const cashAmount = isCashOrOtherPayment
      ? isOtherSplit
        ? value
        : parseToDecimalNumber(valueAfterCalculation)
      : isOtherSplit
      ? parseToDecimalNumber(valueAfterCalculation)
      : value;

    // Calculate trade amount to pay
    const tradeAmount = isCashOrOtherPayment
      ? isOtherSplit
        ? parseToDecimalNumber(valueAfterCalculation)
        : value
      : isOtherSplit
      ? value
      : parseToDecimalNumber(valueAfterCalculation);

    updateTradeData(
      {
        ...newTradeData,
        SubTotal: totalAmount,
        inventory: updatedInventory,
        amountToPay: totalAmount,
        totalAmountToPay: totalAmount,
        currentAmountToPay: totalAmount,

        splitAmounts: {
          ...splitAmounts,
          cashAmount,
          tradeAmount,
          [splitKey]: value,

          ...(isOtherSplit
            ? {
                splitAmount: parseToDecimalNumber(valueAfterCalculation),
              }
            : {
                otherSplitAmount: parseToDecimalNumber(valueAfterCalculation),
                originalAmountToPay: parseToDecimalNumber(
                  valueAfterCalculation
                ),
              }),
        },
      },
      SALE_TRANSACTION_ACTIVITY_TYPES.SPLIT_AMOUNT
    );
  };
  let firstFieldLabel = EMPTY_STRING;
  let secondFieldLabel = EMPTY_STRING;
  let firstFieldValue = 0;
  let secondFieldValue = 0;
  let firstFieldMaxValue = 0;
  let secondFieldMaxValue = 0;
  /*
    This component must only render when the isSplitPayment is true as if user is not splitting then don't render this component (parent has the conditional render) 
    There are two input fields and each one can represent store credit, cash, other based on the values saved in splitAmounts state so a runtime decision is made which value is saved in which one and which label should represent the first field and which label should represent the second field
    isCashPayment true means user has selected cash payment initially (the cart screen of trade) and this means otherSplitAmount will have cash value and splitAmount will have storeCredit value
    isCashPayment false means user has selected the store credit payment initially (the cart screen of trade) and this means otherSplitAmount will have store credit value and splitAmount will have cash value. Cash and other is same so a seprate variable for this type does not exist in the splitAmounts state of trade, (it does exist in the payment history but not in splitAmounts state, here we just use a different label for it) 
  */
  // user selected cash as the payment method on the transaction screen hence render the cash field as the first field
  if (isCashPayment) {
    if (isOtherPayment) {
      firstFieldLabel = transactionConstants.OTHER_LABEL;
      secondFieldLabel = transactionConstants.STORE_CREDIT_LABEL;
    } else {
      firstFieldLabel = transactionConstants.CASH_LABEL;
      secondFieldLabel = transactionConstants.STORE_CREDIT_LABEL;
    }
  }
  // User selected storeCredit payment in the previous screen and click pay then isCashPayment will be false hence rendering the storeCredit field as the first field
  else if (!isCashPayment) {
    if (isOtherPayment) {
      firstFieldLabel = transactionConstants.STORE_CREDIT_LABEL;
      secondFieldLabel = transactionConstants.OTHER_LABEL;
    } else {
      firstFieldLabel = transactionConstants.STORE_CREDIT_LABEL;
      secondFieldLabel = transactionConstants.CASH_LABEL;
    }
  }
  firstFieldValue = splitAmounts.otherSplitAmount;
  firstFieldMaxValue = Number(splitAmounts.otherAmountToAdjust);
  secondFieldValue = splitAmounts.splitAmount;
  secondFieldMaxValue = splitAmounts.amountToAdjust;

  return (
    <>
      <Col md={8} className="d-flex flex-column align-items-end mt-2">
        <span className="amount-to-pay-sub-heading-trade">
          {`${firstFieldLabel} :`}
        </span>
        <span className="">
          Enter up to{" "}
          <span className="fw-bold">
            {" "}
            {`$${parseToThousandSeperatorDecimalNumber(firstFieldMaxValue)}`}
          </span>
        </span>
      </Col>
      <Col
        md={4}
        className="d-flex justify-content-end align-items-center amount-to-pay-detail-trade mt-2"
      >
        <SimpleNumberField
          key={inputFieldsKeyRef.current}
          value={firstFieldValue}
          className="split-number-field"
          onChange={(value) => {
            handleSplitValueChange(value, "otherSplitAmount", true);
          }}
          maxValue={firstFieldMaxValue}
          IconImage={DollarIcon}
        />
      </Col>
      <Col md={8} className="d-flex flex-column align-items-end mt-2">
        <span className="amount-to-pay-sub-heading-trade">
          {`${secondFieldLabel}:`}
        </span>
        <span className="">
          Enter up to{" "}
          <span className="fw-bold">
            {" "}
            {`$${parseToThousandSeperatorDecimalNumber(secondFieldMaxValue)}`}
          </span>
        </span>
      </Col>
      <Col
        md={4}
        className="d-flex justify-content-end align-items-center amount-to-pay-detail-trade mt-2"
      >
        <SimpleNumberField
          key={inputFieldsKeyRef.current}
          value={secondFieldValue}
          className="split-number-field"
          onChange={(value) => {
            handleSplitValueChange(value);
          }}
          maxValue={Number(secondFieldMaxValue)}
          IconImage={DollarIcon}
        />
      </Col>
    </>
  );
};

export default SplitPaymentInputFields;
